import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

/* Phase 11.1 — colors per METRIC (mirrors the chart series), NOT per value.
   The value text itself is always neutral (foreground/85). Each tile carries a
   left-edge color stripe + colored uppercase label so it visually maps to the
   corresponding line in the Distribution / Career / Timeline charts. */
const METRIC_COLOR = {
  overall:   "#10B981", // emerald
  finishing: "#3B82F6", // blue
  passing:   "#EAB308", // yellow
  defending: "#EF4444", // red
};

/** Compact roster cell — integer percent (ROUNDUP), NEUTRAL color.
 *  Returns em-dash when value is null/empty (Phase 11.1 — Issue 4). */
export function FbmStatCell({ value, testId }) {
  if (value == null || value === "") {
    return <span className="text-muted-foreground font-mono" data-testid={testId}>—</span>;
  }
  const v = Number(value);
  return (
    <span className="font-mono font-semibold text-foreground/85" data-testid={testId}>
      {Math.ceil(v)}
    </span>
  );
}

/** Phase 11.6 — value-graded variant for the LEAGUE drill-down table only.
 *  ≥70 emerald, 40–69 amber, <40 rose. Same thresholds as pre-Phase 11.1.
 *  Do NOT use elsewhere — the unbounded charts on detail pages own the color
 *  vocabulary now. */
export function FbmStatCellGraded({ value, testId }) {
  if (value == null || value === "") {
    return <span className="text-muted-foreground font-mono" data-testid={testId}>—</span>;
  }
  const v = Number(value);
  const tone =
    v >= 70 ? "text-emerald-500" :
    v >= 40 ? "text-amber-500" :
              "text-rose-500";
  return (
    <span className={`font-mono font-semibold ${tone}`} data-testid={testId}>
      {Math.ceil(v)}
    </span>
  );
}

/** Big tile on the detail page — one-decimal percent (ROUNDUP).
 *  Phase 11.1: metric-colored accent (left stripe + label), neutral value text,
 *  and graceful "—" rendering when the backend returns null for empty ranges. */
export function FbmStatTile({ label, value, description, testId, metric }) {
  const v = value == null ? null : Number(value);
  const display = v == null ? "—" : (Math.ceil(v * 10) / 10).toFixed(1);
  const accent = METRIC_COLOR[metric] || null;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-testid={testId}
            title={description}
            tabIndex={0}
            className="relative rounded-xl border border-border bg-card p-5 cursor-help transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 hover:border-foreground/20 overflow-hidden"
            style={accent ? { borderLeftColor: accent, borderLeftWidth: 3 } : undefined}
          >
            <div
              className="text-xs uppercase tracking-[0.18em] mb-3 flex items-center gap-2"
              style={{ color: accent || undefined }}
            >
              {accent && <span className="inline-block h-2 w-2 rounded-full" style={{ background: accent }} />}
              {!accent && <span className="text-muted-foreground">{label}</span>}
              {accent && <span>{label}</span>}
            </div>
            <div className="font-display text-5xl font-bold leading-none text-foreground/85">
              {display}
              {v != null && <span className="text-2xl opacity-60">%</span>}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          sideOffset={8}
          className="max-w-xs text-xs leading-snug bg-popover text-popover-foreground border border-border shadow-md px-3 py-2"
          data-testid={`${testId}-tooltip`}
        >
          {description}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export const FBM_DESCRIPTIONS = {
  overall:   "The probability that a player is able to contribute to the overall play of the team.",
  finishing: "The probability that a player is able to contribute to the finishing of the team.",
  passing:   "The probability that a player is able to contribute to the passing of the team.",
  defending: "The probability that a player is able to contribute to the defending of the team.",
};
